import { useCallback, useDeferredValue, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import useEmblaCarousel from 'embla-carousel-react';
import { Briefcase, ChevronLeft, ChevronRight } from 'lucide-react';
import { useDeals } from '@/hooks/useDeals';
import { MobileSearchBar } from '@/components/mobile/MobileSearchBar';
import { STAGE_ORDER, STAGE_CONFIG, type DealStage } from '@/constants/dealStages';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

const STAGE_LABELS: Record<DealStage, string> = {
  new: 'Novo',
  contact: 'Contato',
  scheduled: 'Agendado',
  proposal: 'Proposta',
  negotiation: 'Negociação',
  won: 'Ganho',
  lost: 'Perdido',
};

function formatMoney(value?: number | null, currency?: string | null) {
  if (typeof value !== 'number') return '-';
  const code = currency || 'BRL';
  try {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: code }).format(value);
  } catch {
    return `${value.toFixed(2)} ${code}`;
  }
}

function contactName(contact: any) {
  if (!contact) return 'Sem contato';
  return `${contact.first_name || ''} ${contact.last_name || ''}`.trim() || 'Sem contato';
}

function DealCardSkeleton() {
  return (
    <div className="rounded-lg border border-l-4 border-l-muted bg-card p-4 shadow-sm space-y-3">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-3 w-1/2" />
        </div>
        <Skeleton className="h-4 w-20 ml-2" />
      </div>
      <Skeleton className="h-3 w-1/3" />
    </div>
  );
}

export default function MobileDeals() {
  const [search, setSearch] = useState('');
  const deferredSearch = useDeferredValue(search);
  const [activeIndex, setActiveIndex] = useState(0);
  const { dealsByStage, stageCounts, loading, error } = useDeals({ stageFilter: 'all' });

  const [emblaRef, emblaApi] = useEmblaCarousel({ loop: false, align: 'start', dragFree: false });

  const onSelect = useCallback(() => {
    if (!emblaApi) return;
    setActiveIndex(emblaApi.selectedScrollSnap());
  }, [emblaApi]);

  useEffect(() => {
    if (!emblaApi) return;
    onSelect();
    emblaApi.on('select', onSelect);
    return () => { emblaApi.off('select', onSelect); };
  }, [emblaApi, onSelect]);

  const scrollPrev = useCallback(() => emblaApi?.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi?.scrollNext(), [emblaApi]);

  const activeStage = STAGE_ORDER[activeIndex];
  const stageConfig = STAGE_CONFIG[activeStage];
  const count = stageCounts[activeStage]?.count || 0;

  const filteredDealsByStage = useMemo(() => {
    const term = deferredSearch.trim().toLowerCase();
    if (!term) return dealsByStage;

    const result: Record<string, any[]> = {};
    for (const stage of STAGE_ORDER) {
      const deals = dealsByStage[stage] || [];
      result[stage] = deals.filter((deal: any) => {
        const name = (deal.name || '').toLowerCase();
        const contact = contactName(deal.contact).toLowerCase();
        return name.includes(term) || contact.includes(term);
      });
    }
    return result;
  }, [dealsByStage, deferredSearch]);

  // Total value for active stage
  const stageTotal = useMemo(() => {
    const deals = filteredDealsByStage[activeStage] || [];
    return deals.reduce((sum: number, deal: any) => sum + (Number(deal.total_amount) || 0), 0);
  }, [filteredDealsByStage, activeStage]);

  const activeStageCurrency = useMemo(() => {
    const deals = filteredDealsByStage[activeStage] || [];
    const first = deals.find((d: any) => d.currency);
    return first?.currency || 'BRL';
  }, [filteredDealsByStage, activeStage]);

  return (
    <div className="flex flex-col h-full">
      {/* Stage indicator */}
      <div className={cn("flex items-center justify-between px-4 py-4", stageConfig.bgColor)}>
        <button
          onClick={scrollPrev}
          disabled={activeIndex === 0}
          className={cn(
            "p-1.5 rounded-full transition-all",
            activeIndex === 0 ? "opacity-30" : "opacity-80 hover:bg-background/30 active:scale-95"
          )}
          aria-label="Estágio anterior"
        >
          <ChevronLeft className={cn("h-5 w-5", stageConfig.color)} />
        </button>
        <div className="flex flex-col items-center gap-0.5">
          <div className="flex items-center gap-2">
            <span className={cn("text-sm font-bold tracking-tight", stageConfig.color)}>
              {STAGE_LABELS[activeStage]}
            </span>
            <span className={cn(
              "inline-flex items-center justify-center rounded-full px-2.5 py-0.5 text-xs font-semibold min-w-[1.75rem]",
              stageConfig.countBg, stageConfig.countText
            )}>
              {count}
            </span>
          </div>
          {stageTotal > 0 && (
            <span className="text-xs text-muted-foreground font-medium">
              Total: {formatMoney(stageTotal, activeStageCurrency)}
            </span>
          )}
        </div>
        <button
          onClick={scrollNext}
          disabled={activeIndex === STAGE_ORDER.length - 1}
          className={cn(
            "p-1.5 rounded-full transition-all",
            activeIndex === STAGE_ORDER.length - 1 ? "opacity-30" : "opacity-80 hover:bg-background/30 active:scale-95"
          )}
          aria-label="Próximo estágio"
        >
          <ChevronRight className={cn("h-5 w-5", stageConfig.color)} />
        </button>
      </div>

      {/* Dots */}
      <div className="flex justify-center gap-1.5 py-2">
        {STAGE_ORDER.map((stage, i) => (
          <button
            key={stage}
            className={cn(
              "h-1.5 rounded-full transition-all",
              i === activeIndex ? "w-4 bg-primary" : "w-1.5 bg-muted-foreground/30"
            )}
            onClick={() => emblaApi?.scrollTo(i)}
            aria-label={STAGE_LABELS[stage]}
          />
        ))}
      </div>

      {/* Search */}
      <div className="px-3 pb-2">
        <MobileSearchBar
          value={search}
          onChange={setSearch}
          placeholder="Buscar negócio..."
          ariaLabel="Buscar negócio"
        />
      </div>

      {error && <p className="text-sm text-destructive px-3 py-2">Erro ao carregar negócios.</p>}

      {/* Carousel */}
      <div className="flex-1 overflow-hidden" ref={emblaRef}>
        <div className="flex h-full">
          {STAGE_ORDER.map((stage) => {
            const deals = filteredDealsByStage[stage] || [];
            const cfg = STAGE_CONFIG[stage];
            return (
              <div key={stage} className="min-w-0 shrink-0 grow-0 basis-full h-full overflow-y-auto px-3 pb-3">
                {loading ? (
                  <div className="space-y-3 pt-2">
                    <DealCardSkeleton />
                    <DealCardSkeleton />
                    <DealCardSkeleton />
                  </div>
                ) : deals.length === 0 ? (
                  <div className="flex flex-col items-center justify-center pt-16 gap-3">
                    <div className={cn("rounded-full p-4", cfg.bgColor)}>
                      <Briefcase className={cn("h-8 w-8", cfg.color)} />
                    </div>
                    <p className="text-sm text-muted-foreground text-center">
                      Nenhum negócio em <span className="font-medium">{STAGE_LABELS[stage]}</span>
                    </p>
                  </div>
                ) : (
                  <div className="space-y-2 pt-1">
                    {deals.map((deal: any) => (
                      <Link
                        key={deal.id}
                        to={`/m/deals/${deal.id}`}
                        className={cn(
                          "flex items-center gap-3 rounded-lg border border-l-4 bg-card p-3.5 shadow-sm",
                          "active:scale-[0.98] transition-transform",
                          cfg.borderColor
                        )}
                      >
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-semibold truncate text-foreground">
                            {deal.name || 'Negócio sem nome'}
                          </p>
                          <p className="text-xs text-muted-foreground truncate mt-0.5">
                            {contactName(deal.contact)}
                          </p>
                          {deal.updated_at && (
                            <p className="text-[11px] text-muted-foreground/60 mt-1">
                              {new Date(deal.updated_at).toLocaleDateString('pt-BR')}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-1.5 shrink-0">
                          <span className="text-sm font-semibold text-foreground whitespace-nowrap">
                            {formatMoney(deal.total_amount, deal.currency)}
                          </span>
                          <ChevronRight className="h-4 w-4 text-muted-foreground/40" />
                        </div>
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
